import { useCallback } from "react";
import { csvUrl, money, periodApi, THREE_LABELS } from "../../api/periods";
import { LoadState, useLoad } from "../admin/shared";

export function MyPay({ periodId }: { periodId: string }) {
  const loader = useCallback(() => periodApi.myPay(periodId), [periodId]);
  const { data, error } = useLoad(loader);
  const s = data?.summary;
  return (
    <section className="space-y-3 rounded-sm border border-white/10 bg-surface p-5" data-testid="my-pay-section">
      <p className="label-caps text-signal">My Pay</p>
      <LoadState error={error} loading={!data && !error} testId="my-pay-load" />
      {data && !s && <p className="text-sm text-zinc-500" data-testid="my-pay-empty">No pay period to show yet.</p>}
      {s && (
        <>
          <p className="text-xs text-zinc-400" data-testid="my-pay-status">{s.period.start_date} – {s.period.end_date} · {s.status}</p>
          <p className="text-sm text-zinc-200" data-testid="my-pay-hours">Paid {s.paid_hours} h · regular {s.regular_hours} h · overtime {s.ot_hours} h</p>
          {s.unavailable_note ? <p className="text-sm text-amber-200" data-testid="my-pay-unavailable">{s.unavailable_note}</p> : (
            <>
              <ul className="text-xs text-zinc-300" data-testid="my-pay-rates">{s.rates.map((r) => <li key={`${r.role}-${r.rate_cents}`}>{r.role}: {money(r.rate_cents)}/h</li>)}</ul>
              <dl className="grid gap-2 sm:grid-cols-3" data-testid="my-pay-three-totals">
                {THREE_LABELS.map(([k, l]) => <div key={k} data-testid={`my-pay-${k}`}><dt className="text-xs text-zinc-500">{l}</dt><dd className={k === "total_gross_cents" ? "font-display text-3xl font-bold text-white" : "font-mono text-lg text-zinc-200"}>{money(s[k])}</dd></div>)}
              </dl>
              {s.estimate && <p className="text-xs text-amber-200" data-testid="my-pay-estimate">Estimate</p>}
              {s.adjustments.length > 0 && <ul className="text-xs text-zinc-300" data-testid="my-pay-adjustments">{s.adjustments.map((a, i) => <li key={i}>Adjustment · {a.type} · {money(a.amount_cents)} · {a.reason}</li>)}</ul>}
            </>
          )}
          <div className="rounded-sm border border-white/10 p-3 text-sm" data-testid="my-pay-payroll">
            {s.payroll.net_pay_cents == null ? <p className="text-zinc-300" data-testid="my-pay-net-not-finalized">Net pay: Not finalized</p> : (
              <>
                <p className="text-zinc-300">Total gross <span className="font-mono text-white">{money(s.payroll.total_gross_cents)}</span></p>
                <ul className="text-xs text-zinc-400" data-testid="my-pay-deductions">{s.payroll.deductions?.map((d, i) => <li key={i}>{d.label}: {money(d.amount_cents)}</li>)}</ul>
                <p className="font-display text-2xl font-bold text-white" data-testid="my-pay-net">Net pay {money(s.payroll.net_pay_cents)}</p>
                <p className="text-xs text-zinc-500">{s.payroll.note} Payment method: {s.payroll.payment_method}</p>
                <p className="text-sm text-white" data-testid="my-pay-payment-status">Payment: {s.payroll.payment_status?.label ?? "Not yet paid"}{s.payroll.payment_status?.reference_last4 ? ` · ${s.payroll.payment_status.method} ref …${s.payroll.payment_status.reference_last4}` : ""}</p>
              </>
            )}
          </div>
          <p className="text-xs font-semibold text-amber-100" data-testid="my-pay-label">{s.label}</p>
          {s.status.startsWith("Finalized") && <a href={csvUrl("/me/pay/export.csv", { period: s.period.id })} className="text-xs font-semibold text-signal hover:text-white" data-testid="my-pay-csv">Download my pay summary (CSV)</a>}
        </>
      )}
    </section>
  );
}
